/**
 * Предложение установить приложение как сценарий (PWA-01, PWA-02, PWA-07).
 *
 * Хук отвечает только за «можно ли и что показать» и за запись отметок
 * частоты. Условия контекста (корневой экран, отсутствие блокирующих
 * состояний, активный набор сообщения) решает экран-владелец: они про
 * интерфейс, а не про установку.
 */
import { useCallback, useEffect, useState } from 'react'

import {
  countInstallPromptShow,
  dismissInstallPrompt,
  hideInstallPromptForever,
  installVariant,
  mayShowBanner,
  promptInstall,
  readInstallPrompt,
  subscribeInstallState,
  type InstallPromptRecord,
  type InstallVariant,
} from '@shared/pwa/install'

export interface InstallOffer {
  variant: InstallVariant
  /** Правило частоты позволяет показать баннер (PWA-01 / PWA-07). */
  allowed: boolean
  /** Инструкция PWA-02 открыта. */
  instructionsOpen: boolean
  openInstructions: () => void
  closeInstructions: () => void
  /** «Установить» — системный диалог; вызывать из обработчика жеста. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  /** «Позже» и крестик. */
  dismiss: () => void
  /** «Уже установил(а)». */
  hideForever: () => void
}

export function useInstallOffer(userId: string | null): InstallOffer {
  const [variant, setVariant] = useState<InstallVariant>(() => installVariant())
  const [record, setRecord] = useState<InstallPromptRecord | null>(null)
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  // Событие `beforeinstallprompt` может прийти уже после первого рендера.
  useEffect(() => subscribeInstallState(() => setVariant(installVariant())), [])

  useEffect(() => {
    if (!userId) {
      setRecord(null)
      return
    }

    let cancelled = false
    void readInstallPrompt(userId)
      .then((value) => {
        if (!cancelled) setRecord(value)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [userId])

  const apply = useCallback((next: InstallPromptRecord) => setRecord(next), [])

  const dismiss = useCallback(() => {
    if (!userId) return
    void dismissInstallPrompt(userId).then(apply)
  }, [apply, userId])

  const hideForever = useCallback(() => {
    if (!userId) return
    void hideInstallPromptForever(userId).then(apply)
  }, [apply, userId])

  const install = useCallback(async () => {
    const outcome = await promptInstall()
    // §9.4 п. 2: `dismissed` считается отказом для правила частоты.
    if (outcome === 'dismissed') dismiss()
    if (outcome === 'accepted') hideForever()
    return outcome
  }, [dismiss, hideForever])

  return {
    variant,
    // Счётчик показов растёт по ДЕЙСТВИЮ пользователя (PWA-01/PWA-02
    // «Действия → результат»), поэтому сам факт отрисовки его не меняет
    // и баннер не исчезает сразу после появления.
    allowed: record ? mayShowBanner(record, variant) : false,
    instructionsOpen,
    openInstructions: () => setInstructionsOpen(true),
    closeInstructions: () => {
      // PWA-02 п. 1: «Понятно» засчитывается как показ.
      if (userId) void countInstallPromptShow(userId).then(apply)
      setInstructionsOpen(false)
    },
    install,
    dismiss,
    hideForever,
  }
}
